import {Injectable} from '@angular/core'
import {Cell, DataType, Header, Row, TableSortDirection, TableSorting} from './baseTable.interface'
import Utils from '../../../../utils/utils'
import {LoggerService} from "../../../../services/logger/logger.service";
import {TranslateService} from "../../../../services/translate/translate.service";
import * as moment from 'moment';

@Injectable()
export class SortService {
    private activeSorting: TableSorting = null;
    
    constructor(protected logger: LoggerService) {
    }
    
    public getActiveSorting(): TableSorting {
        return this.activeSorting;
    }
    
    public sortByColumn(rows: Row[], columnId: string | number, headers: Header[], direction?: TableSortDirection, firstSort = false): void {
        headers.forEach( (header) => {
            if (header.columnId === columnId && header.code === 'icon' && firstSort) {
                columnId = Number(columnId) + 1;
            }
            if (header.isVisible && firstSort) {
                direction = TableSortDirection.SORT_DIRECTION_DESC;
            }
        })
        this.logger.log(Utils.measureFunctionTime('[SortService] Column sorted in: ', () => {
            this.updateHeaderSortDirections(headers, columnId, direction);
            this.sortRowsByColumn(rows, columnId, headers);
            this.setActiveSorting(headers, columnId);
        }));
    }
    
    private setActiveSorting(headers: Header[], columnId: string | number): void {
        this.activeSorting = {
            columnId: columnId,
            sortDirection: this.getSortDirectionByColumnID(headers, columnId)
        };
    }
    
    private updateHeaderSortDirections(headers: Header[], columnId: string | number, direction?: TableSortDirection): void {
        headers.forEach((header: Header) => {
            if (header.columnId === columnId) {
                if (direction === undefined) {
                    switch (header.sortDirection) {
                        case TableSortDirection.SORT_DIRECTION_ASC :
                            header.sortDirection = TableSortDirection.SORT_DIRECTION_DESC;
                            break;
                        default:
                            header.sortDirection = TableSortDirection.SORT_DIRECTION_ASC;
                            break;
                    }
                } else {
                    header.sortDirection = direction;
                }
            } else {
                header.sortDirection = TableSortDirection.SORT_DIRECTION_UNSET;
            }
        });
    }
    
    private sortRowsByColumn(rows: Row[], columnId: string | number, headers: Header[]): void {
        const isAscending: boolean = this.getSortDirectionByColumnID(headers, columnId) === TableSortDirection.SORT_DIRECTION_ASC;
        
        rows.sort((rowA: Row, rowB: Row) => {
            const cellA: Cell = this.getCellInRowByColumnID(rowA, columnId);
            const cellB: Cell = this.getCellInRowByColumnID(rowB, columnId);
            let cellDataType = cellA?.dataType;
            let cellLabelA: string = cellA?.label;
            let cellLabelB: string = cellB?.label;
            
            if (cellDataType === DataType.COMPLEX) {
                cellDataType = DataType.STRING;
                cellLabelA = cellA?.children.reduce((string, cellChild) => {
                    return string + cellChild.label;
                }, '');
                cellLabelB = cellB?.children.reduce((string, cellChild) => {
                    return string + cellChild.label;
                }, '');
            }
            
            if (cellDataType === DataType.DATETIME || cellDataType === DataType.DATE) {
                return this.compareDateTimeStrings(cellLabelA, cellLabelB, isAscending);
            }
            
            if (cellDataType === DataType.NUMBER) {
                return this.compareNumbers(Number(cellLabelA), Number(cellLabelB), isAscending);
            }
            
            return this.compareStrings(cellLabelA, cellLabelB, isAscending);
        });
    }
    
    private getSortDirectionByColumnID(headers: Header[], columnId: string | number): TableSortDirection {
        if (headers.some((header: Header) => header.columnId === columnId)) {
            return headers.find((header: Header) => header.columnId === columnId).sortDirection;
        } else {
            return TableSortDirection.SORT_DIRECTION_ASC;
        }
    }
    
    private getCellInRowByColumnID(row: Row, columnId: string | number): Cell {
        return row.cells.find((cell: Cell) => cell.columnId === columnId);
    }
    
    public compareDateTimeStrings(dateTimeStringA: string, dateTimeStringB: string, isAscending: boolean): number {
        const locale = TranslateService.LOCALE || 'nl-NL';
        moment.locale(locale);

        const localeData = moment.localeData(locale);
        const localeFormats = [
            moment.ISO_8601,
            localeData.longDateFormat('L'),
            localeData.longDateFormat('LL'),
            localeData.longDateFormat('LT'),
            localeData.longDateFormat('LTS'),
            localeData.longDateFormat('lll'),
            localeData.longDateFormat('LLL'),
            localeData.longDateFormat('llll'),
            localeData.longDateFormat('L') + ' HH:mm:ss',
            localeData.longDateFormat('L') + ' HH:mm',
            localeData.longDateFormat('L') + ', HH:mm:ss',
            localeData.longDateFormat('L') + ', HH:mm'
        ].filter((format, index, arr) => format && arr.indexOf(format) === index);

        const guessedFallbackFormats = [
            'YYYY-MM-DD',
            'YYYY-MM-DD HH:mm:ss',
            'YYYY-MM-DD HH:mm',
            'DD-MM-YYYY',
            'DD-MM-YYYY HH:mm:ss',
            'DD-MM-YYYY HH:mm',
            'DD.MM.YYYY',
            'DD.MM.YYYY HH:mm:ss',
            'DD.MM.YYYY HH:mm',
            'DD/MM/YYYY',
            'DD/MM/YYYY HH:mm:ss',
            'DD/MM/YYYY HH:mm',
            'MM/DD/YYYY',
            'MM/DD/YYYY HH:mm:ss',
            'MM/DD/YYYY HH:mm'
        ].filter(format => !localeFormats.includes(format));

        const toTimestamp = (str: string): number => {
            if (!str) return 0;

            const value = String(str).trim();
            const direct = Date.parse(value);
            if (!isNaN(direct)) {
                return direct;
            }

            for (const format of localeFormats) {
                
                const parsed = moment(value, format, true);
                if (parsed.isValid()) {
                    return parsed.valueOf();
                }
            }

            for (const format of guessedFallbackFormats) {
                const parsed = moment(value, format, true);
                if (parsed.isValid()) {
                    return parsed.valueOf();
                }
            }

            return 0;
        };

        return this.compareNumbers(toTimestamp(dateTimeStringA), toTimestamp(dateTimeStringB), isAscending);
    }

    public compareValues(valueA: string | number | Date | null, valueB: string | number | Date | null, isAscending: boolean, dataType?: DataType): number {
        if (dataType === DataType.DATETIME || dataType === DataType.DATE) {
            return this.compareDateTimeStrings(String(valueA ?? ''), String(valueB ?? ''), isAscending);
        }

        if (dataType === DataType.NUMBER) {
            return this.compareNumbers(Number(valueA ?? 0), Number(valueB ?? 0), isAscending);
        }

        return this.compareStrings(String(valueA ?? ''), String(valueB ?? ''), isAscending);
    }

    public compareStrings(stringA: string, stringB: string, isAscending: boolean): number {
        const locale = TranslateService.LOCALE || 'nl-NL';
        const comparison = String(stringA ?? '').localeCompare(
            String(stringB ?? ''),
            locale,
            { numeric: true, sensitivity: 'base' }
        );

        return isAscending ? comparison : -comparison;
    }
    
    private compareNumbers(numberA: number, numberB: number, isAscending: boolean) {
        if (numberA < numberB) {
            return isAscending ? -1 : 1;
        }
        if (numberA > numberB) {
            return isAscending ? 1 : -1;
        }
        return 0;
    }
}
